import React, { useEffect, useState } from 'react';

const STORAGE_KEY = 'felgocar_easter_2026_closed';

const EasterPopup: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [animateOut, setAnimateOut] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem(STORAGE_KEY)) return;

        // Admin decyduje — pobierz ustawienie z API
        fetch('/api/settings/easter_popup_enabled')
            .then(r => r.ok ? r.json() : null)
            .then(val => {
                // Jeśli admin włączył (lub API nie odpowiedziało i wartość null → nie pokazuj)
                const enabled = val === 'true' || val === true;
                if (!enabled) return;
                setTimeout(() => setVisible(true), 2000);
            })
            .catch(() => {
                // Jeśli błąd API — nie pokazuj (bezpieczna domyślna)
            });
    }, []);

    const close = () => {
        setAnimateOut(true);
        setTimeout(() => {
            setVisible(false);
            sessionStorage.setItem(STORAGE_KEY, '1');
        }, 400);
    };

    if (!visible) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                style={{ animation: animateOut ? 'fadeOut 0.4s ease forwards' : 'fadeIn 0.3s ease' }}
                onClick={close}
            />

            {/* Kartka */}
            <div
                className="fixed z-[9999] inset-0 flex items-center justify-center pointer-events-none px-4"
            >
                <div
                    className="pointer-events-auto relative max-w-lg w-full"
                    style={{
                        animation: animateOut
                            ? 'popDown 0.4s cubic-bezier(0.4,0,0.2,1) forwards'
                            : 'popUp 0.5s cubic-bezier(0.34,1.56,0.64,1)',
                    }}
                >
                    {/* Tło kartki */}
                    <div className="relative overflow-hidden rounded-3xl shadow-2xl border border-yellow-400/30"
                        style={{
                            background: 'linear-gradient(135deg, #0b1220 0%, #0f1f0f 40%, #1a1a00 70%, #0b1220 100%)',
                        }}
                    >
                        {/* Dekoracyjne tło — zielona trawa i niebo */}
                        <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none select-none">
                            {/* Trawa */}
                            <div className="absolute bottom-0 left-0 right-0 h-24"
                                style={{ background: 'linear-gradient(to top, #1a3a0a, transparent)' }} />
                            {/* Promienie słońca */}
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="absolute top-0 left-1/2 origin-top"
                                    style={{
                                        width: '2px',
                                        height: '200px',
                                        background: 'linear-gradient(to bottom, rgba(255,220,50,0.6), transparent)',
                                        transform: `translateX(-50%) rotate(${i * 45}deg)`,
                                    }} />
                            ))}
                        </div>

                        {/* Pływające emojis — zając, jajka, felga */}
                        <div className="absolute top-3 left-4 text-4xl animate-bounce" style={{ animationDelay: '0.1s', animationDuration: '2.2s' }}>🐇</div>
                        <div className="absolute top-5 right-10 text-3xl animate-bounce" style={{ animationDelay: '0.4s', animationDuration: '1.9s' }}>🥚</div>
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 text-2xl animate-bounce" style={{ animationDelay: '0.7s', animationDuration: '2.5s' }}>🌸</div>
                        <div className="absolute bottom-14 left-5 text-2xl animate-bounce" style={{ animationDelay: '0.2s', animationDuration: '2.1s' }}>🥚</div>
                        <div className="absolute bottom-16 right-6 text-3xl animate-bounce" style={{ animationDelay: '0.9s', animationDuration: '1.8s' }}>🌷</div>
                        <div className="absolute top-14 right-3 text-2xl animate-spin" style={{ animationDuration: '8s' }}>⚙️</div>

                        {/* Główna zawartość */}
                        <div className="relative z-10 px-8 py-10 text-center">
                            {/* Felga jako słońce / gwóźdź */}
                            <div className="flex justify-center mb-4">
                                <div className="relative">
                                    {/* Felga SVG */}
                                    <svg width="90" height="90" viewBox="0 0 90 90" className="animate-spin" style={{ animationDuration: '12s' }}>
                                        {/* Opona */}
                                        <circle cx="45" cy="45" r="42" fill="none" stroke="#555" strokeWidth="7" />
                                        {/* Rim */}
                                        <circle cx="45" cy="45" r="33" fill="none" stroke="#c0a020" strokeWidth="3" />
                                        {/* Środek */}
                                        <circle cx="45" cy="45" r="8" fill="#c0a020" />
                                        <circle cx="45" cy="45" r="4" fill="#0b1220" />
                                        {/* Szprychy */}
                                        {[0, 72, 144, 216, 288].map((angle, i) => (
                                            <line key={i}
                                                x1="45" y1="45"
                                                x2={45 + 25 * Math.cos((angle - 90) * Math.PI / 180)}
                                                y2={45 + 25 * Math.sin((angle - 90) * Math.PI / 180)}
                                                stroke="#c0a020" strokeWidth="3" strokeLinecap="round"
                                            />
                                        ))}
                                        {/* Śruby */}
                                        {[0, 72, 144, 216, 288].map((angle, i) => (
                                            <circle key={i}
                                                cx={45 + 27 * Math.cos((angle - 90) * Math.PI / 180)}
                                                cy={45 + 27 * Math.sin((angle - 90) * Math.PI / 180)}
                                                r="2.5" fill="#ffd700"
                                            />
                                        ))}
                                    </svg>
                                    {/* Złoty blask */}
                                    <div className="absolute inset-0 rounded-full"
                                        style={{ boxShadow: '0 0 30px rgba(255,215,0,0.4)', borderRadius: '50%' }} />
                                </div>
                            </div>

                            {/* Nagłówek */}
                            <div className="text-4xl mb-1">🐣</div>
                            <h2 className="text-2xl font-bold text-yellow-300 mb-1 tracking-wide">
                                Wesołych Świąt Wielkanocnych!
                            </h2>
                            <p className="text-yellow-500/80 text-sm font-medium mb-5 tracking-widest uppercase">
                                od Zespołu FelgoCar
                            </p>

                            {/* Jajka wielkanocne — dekoracja liniowa */}
                            <div className="flex justify-center gap-2 mb-5">
                                {['#e84393','#22c55e','#3b82f6','#f59e0b','#a855f7'].map((color, i) => (
                                    <div key={i} className="relative" style={{ transform: `rotate(${(i - 2) * 8}deg)` }}>
                                        <svg width="22" height="28" viewBox="0 0 22 28">
                                            <ellipse cx="11" cy="15" rx="10" ry="13" fill={color} opacity="0.9" />
                                            <ellipse cx="11" cy="15" rx="10" ry="13" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                                            {/* Wzór na jajku */}
                                            <path d={`M 3 12 Q 11 9 19 12`} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
                                        </svg>
                                    </div>
                                ))}
                            </div>

                            {/* Treść życzeń */}
                            <div className="bg-white/5 border border-yellow-400/20 rounded-2xl px-6 py-5 mb-6 text-left">
                                <p className="text-gray-100 text-base leading-relaxed">
                                    Niech te Święta toczą się równie <span className="text-yellow-300 font-semibold">gładko jak dobrze wyważona felga</span>,
                                    a każdy zakręt życia pokonasz z lekkością i stylem. 🏎️
                                </p>
                                <p className="text-gray-300 text-sm mt-3 leading-relaxed">
                                    Życzymy radosnych chwil w gronie bliskich,
                                    <strong className="text-white"> zdrowych świąt</strong> i wielu kilometrów
                                    na nowiutkich felgach aluminiowych! 🌿
                                </p>
                                <p className="text-yellow-400/80 text-xs mt-3 italic">
                                    — Zespół FelgoCar: Kamil, Kacper i wszyscy, którzy kręcą kołami w Waszym imieniu 🔧
                                </p>
                            </div>

                            {/* Przycisk CTA */}
                            <button
                                onClick={close}
                                className="w-full py-3 px-6 rounded-xl font-bold text-black text-base transition-all duration-200 hover:scale-105 hover:shadow-lg"
                                style={{
                                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                                    boxShadow: '0 4px 20px rgba(251,191,36,0.4)',
                                }}
                            >
                                🥚 Wracam po felgi! 🥚
                            </button>

                            {/* Mały przycisk zamknij */}
                            <button
                                onClick={close}
                                className="mt-3 text-gray-500 hover:text-gray-300 text-sm transition-colors"
                            >
                                Zamknij
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes fadeOut {
                    from { opacity: 1; }
                    to   { opacity: 0; }
                }
                @keyframes popUp {
                    from { opacity: 0; transform: scale(0.7) translateY(40px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes popDown {
                    from { opacity: 1; transform: scale(1) translateY(0); }
                    to   { opacity: 0; transform: scale(0.8) translateY(30px); }
                }
            `}</style>
        </>
    );
};

export default EasterPopup;
